import type { AssetClassConstants } from '@/constants/AssetClassConstants';
import type { Currency } from '@/constants/Currency';
import type { FactorConstants } from '@/constants/FactorConstants';
import type { Region } from '@/constants/Region';
import type { ReturnCategoryConstants } from '@/constants/ReturnCategoryConstants';
import type { ReturnTypeConstants } from '@/constants/ReturnTypeConstants';
import type { StyleConstants } from '@/constants/StyleConstants';
import type { TimeSeriesEntityTypeConstants } from '@/constants/TimeSeriesEntityTypeConstants';
import type { TrackTypeConstants } from '@/constants/TrackTypeConstants';
import type { LanguageDTO } from '@/types/LanguageDTO';
import type { IBenchmark, IPrivateFundBenchmarks, IStrategy } from '@/types/strategy';
import type { StrategyAttributesDTO } from './StrategyAttributesDTO';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import type { IFactsheetDatum } from '@/types/IFactsheetData';
import type { StrategyItemSubset } from '@/types/StrategyItemSubset';
import type { TimeSeriesEntityStatus } from '@/types/admin/strategies/StrategyStatus';

export interface StrategyFactsheetResponseDTO {
  strategy: StrategyFactsheetBasicInfoDTO;
  translated: StrategyFactsheetLocalizationDTO;
  mstarFundInfo: StrategyFactsheetMstarFundInfoDTO | null;
  evestFundInfo: StrategyFactsheetEvestFundInfoDTO | null;
  withIntelligenceFundInfo: StrategyFactsheetWithIntelligenceFundInfoDTO | null;
  structuredProductInfo: StrategyFactsheetStructuredProductInfoDTO | null;
  availableBenchmarks: string[];
  availableFundBenchmarks: { morningstarId: string; code: string }[];
  associatedPureFactor: string | null;
  associatedAttributes: StrategyAttributesDTO | null;

  strategyApiConfig: {
    /**
     * shouldExposePositions is a flag that we have on the portfolio_list table in the database
     * true: this live portfolio/strategy can be viewed in the analytics page as a portfolio
     * false: this strategy can only be viewed as a strategy, not as a portfolio and has access to only the strategy analytics pages
     */
    shouldExposePositions: boolean;

    /**
     * containsOnlyEquities is a flag that tells us if the live strategy/portfolio contains only equities (no QIS)
     */
    containsOnlyEquities: boolean;
  };
}

/**
 * NOTE: you should not be using *only* this function to check if `itemUnderAnalysis` is a strategy.
 * Most checks should be using `shouldTreatRootItemAsPortfolio` instead.
 *
 * You should only be using this function for type checks.
 *
 * This function will now return false if `datum` is `null`.
 */
export const isStrategyFactsheetResponseDTO = (
  datum: IItemUnderAnalysis | IFactsheetDatum | IStrategy | StrategyItemSubset | null,
): datum is StrategyFactsheetResponseDTO => {
  return datum !== null && 'strategyApiConfig' in datum && 'translated' in datum;
};

export interface StrategyFactsheetBasicInfoDTO {
  id: string;
  code: string;
  type: TimeSeriesEntityTypeConstants;
  shortName: string;
  assetClass: AssetClassConstants;
  currency: Currency;
  region: Region;
  factor: FactorConstants;
  longName: string;
  returnType: ReturnTypeConstants | null;
  returnCategory: ReturnCategoryConstants | null;
  style: StyleConstants | null;
  volTarget: string;
  providerId: string | null;
  provider: string;
  indexSource: string;
  isSuspended: boolean;
  status: TimeSeriesEntityStatus | null;
  retirementDate: string | null;
  deletedAt: string | null;
  trackType: TrackTypeConstants;
  inceptionDate: string | null;
  historyStartDate: string | null;
  effectiveHistoryStartDate: string | null;
  isFuturized: boolean | null;

  description: string | null;
  disclaimer: string | null;
  source: string | null;
  benchmarks: IBenchmark | null;
  managementFee: number | null;
  isin: string[];
  morningStarId: string[];
  ticker: string[];
  ric: string[];
  sedol: string[];
  internalReference: string | null;
  classificationId: number | null;

  // Datafeed thematics only
  theme?: string | null;
  esg?: string | null;
  isFlagship?: boolean | null;

  // Stocks only
  country?: string | null;
  sector?: string | null;
  secondarySector?: string | null;
  exchangeName?: string | null;
  esgRating?: string | null;

  // Substitution proxies only
  isProxy?: boolean;
}

export interface StrategyFactsheetStructuredProductInfoDTO {
  maturity: string | null;
}

export interface StrategyFactsheetLocalizationDTO {
  shortName: LanguageDTO;
  longName: LanguageDTO;
  description: LanguageDTO;
  disclaimer: LanguageDTO;
  indexSource: LanguageDTO;
}

export interface StrategyFactsheetMstarFundInfoDTO {
  domicile: string | null;
  legalStructure: string | null;
  managerNames: string[] | null;
  indexFund: boolean | null;
  ucits: boolean | null;
  benchmarks: IPrivateFundBenchmarks | null;
  dividendFrequency: string | null;
  globalCat: string | null;
  managementFee: number | null;
  performanceFee: number | null;
  etf: boolean;
  etn: boolean;
  syntheticReplication: boolean;
  providerCompany: string | null;
}

export interface StrategyFactsheetEvestFundInfoDTO {
  vehicleId: number;
  status: string | null;
  productId: number | null;
  countryRegistration: string | null;
  legalStructure: string | null;
  ucitsFund: boolean | null;
  productMarketFocus: string | null;
  creditQualityApproach: string | null;
  investmentFocus: string | null;
  investmentApproach: string | null;
  portfolioMgmtStrategy: string | null;
  screeningApproach: string | null;
  derivativeUsage: boolean | null;
  primaryEquityStyle: string | null;
  primaryEquityCapitalization: string | null;
  primaryFixedIncomeStyle: string | null;
  universeName: string | null;
}

export interface StrategyFactsheetWithIntelligenceFundInfoDTO {
  fundId: number;
  primaryStrategy: string | null;
  domicile: string | null;
  primaryContact: string | null;
  issuerName: string | null;
  ucits: boolean | null;
  fundStructure: string | null;
  performanceFee: number | null;
}
